import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import usePageSeo from '@/hooks/usePageSeo';

const values = [
  {
    icon: 'ri-heart-line',
    title: '用心推荐',
    desc: '每一部收录的作品都经过筛选与体验，只为把真正值得一看的治愈好番带到你面前。',
  },
  {
    icon: 'ri-plant-line',
    title: '分类清晰',
    desc: '按治愈、日常、萌系与标签精细分类，让找番不再是大海捞针，而是随心情直达。',
  },
  {
    icon: 'ri-sparkling-2-line',
    title: '持续更新',
    desc: '补番目录不断扩充，新作与冷门佳作同步补充，让你的收藏夹永远有惊喜。',
  },
];

const principles = [
  '以内容质量和治愈体验为先，不堆砌无意义信息',
  '分类与标签准确，帮助用户快速匹配想看的方向',
  '尊重每部作品的独特性，客观呈现适合的观看人群',
  '保持页面简洁轻快，让浏览本身就是一种放松',
];

const testimonials = [
  {
    quote: '在这里找到了很多以前错过的治愈冷门番，补番列表越用越顺心。',
    name: '小橘',
    role: '治愈系爱好者',
  },
  {
    quote: '日常番合集整理得很全，晚上下饭不知道看什么就上来逛逛。',
    name: '阿茶',
    role: '上班族',
  },
  {
    quote: '标签筛选太方便了，按心情点一下就有合适的推荐。',
    name: '柚子',
    role: '学生党',
  },
];

export default function About() {
  usePageSeo({
    title: '关于我们 - 牛奶动漫库 · 治愈番推荐与日常番合集',
    description:
      '了解牛奶动漫库的定位与发展理念，一个专注治愈系、日常番与萌系动画补番目录的温馨内容站，用心推荐、分类清晰、持续更新。',
    keywords: '牛奶动漫库,关于我们,治愈番推荐,日常番合集,补番目录',
    canonical: 'https://cartoplist.github.io/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于牛奶动漫库',
        url: 'https://cartoplist.github.io/about',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '牛奶动漫库',
        url: 'https://cartoplist.github.io/',
        description: '专注治愈系、日常番与萌系动画的补番推荐内容站',
        email: 'contact@milk-anime.example',
      },
    ],
  });

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <header className="bg-background-100/70">
          <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <nav aria-label="面包屑" className="mb-5 flex items-center gap-1.5 text-sm text-foreground-500">
              <Link to="/" className="transition hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-accent-600">
              <span className="inline-block h-1.5 w-6 rounded-full bg-accent-500"></span>
              关于我们
            </div>
            <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              一杯牛奶的时间，遇见下一部好番
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              牛奶动漫库，是一份专注治愈系、日常番与萌系动画的补番目录。
            </p>
          </div>
        </header>

        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div className="overflow-hidden rounded-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=cozy%20warm%20anime%20illustration%20of%20a%20person%20enjoying%20a%20hot%20cup%20of%20milk%20by%20a%20sunny%20window%20reading%20a%20book%2C%20peaceful%20healing%20mood%2C%20soft%20golden%20light%2C%20pastel%20warm%20colors%2C%20anime%20background%20art&width=900&height=700&seq=about-story&orientation=portrait"
                alt="牛奶动漫库品牌故事温馨插画"
                title="牛奶动漫库 温馨补番理念"
                className="h-full w-full object-cover object-center"
              />
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-accent-600">
                <span className="inline-block h-1.5 w-6 rounded-full bg-accent-500"></span>
                品牌介绍
              </div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                从一份想好好补番的心情出发
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                越来越多的人在忙碌之后，只想挑一部温暖、轻松、能治愈自己的动画。可是补番找番常常让人头疼——不知道看什么、找不到合心意的、冷门佳作总是被错过。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                于是我们创建了牛奶动漫库，把治愈系、日常番与萌系动画按照题材和标签整理成一份清晰的补番目录。把找番的烦恼交给我们，把看番的愉悦留给你。
              </p>
            </div>
          </div>
        </section>

        <section className="bg-background-100/70">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <div className="mb-8 text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们的定位与理念
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                做一个温暖的补番内容站，让每一次打开都像回到家里一样安心。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {values.map((item) => (
                <div key={item.title} className="rounded-2xl bg-background-50 p-6">
                  <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-2xl`}></i>
                  </span>
                  <h3 className="mb-2 text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-accent-600">
                <span className="inline-block h-1.5 w-6 rounded-full bg-accent-500"></span>
                内容原则
              </div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们坚持这样的内容标准
              </h2>
              <ul className="mt-6 space-y-4">
                {principles.map((item, index) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-sm font-semibold text-accent-700">
                      {index + 1}
                    </span>
                    <span className="text-sm leading-relaxed text-foreground-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl bg-background-100/70 p-6 md:p-8">
              <div className="mb-5 flex items-center gap-2 text-sm font-medium text-accent-600">
                <i className="ri-double-quotes-l text-xl"></i>
                大家的口碑
              </div>
              <div className="space-y-4">
                {testimonials.map((t) => (
                  <figure key={t.name} className="rounded-xl bg-background-50 p-5">
                    <p className="text-sm leading-relaxed text-foreground-700">“{t.quote}”</p>
                    <figcaption className="mt-3 text-sm font-medium text-foreground-950">
                      {t.name}
                      <span className="ml-2 text-xs font-normal text-foreground-500">{t.role}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16 md:px-6 md:pb-20">
          <div className="rounded-2xl bg-primary-100/80 px-6 py-10 text-center md:px-10">
            <h2 className="font-heading text-2xl font-bold text-primary-900">想和我们聊聊？</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-primary-800">
              如果你有想推荐给大家的治愈番，或对补番目录有任何建议，欢迎给我们来信。
            </p>
            <a
              href="mailto:contact@milk-anime.example"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-mail-send-line"></i>
              contact@milk-anime.example
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}